import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClientFlags } from "@/components/ClientFlags";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, User, MapPin, LogIn, LogOut, Sparkles, Save, Loader2,
  CheckCircle2, Lock, KeyRound, Navigation, HeartPulse, AlertCircle, DoorOpen,
  Camera, Trash2, TriangleAlert, X,
} from "lucide-react";
import { statusTone } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";

export default function VisitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCarer = user?.role === "carer";
  const canManage = ["admin", "office"].includes(user?.role);
  const [visit, setVisit] = useState(null);
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState("");
  const [mileage, setMileage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [earlyOpen, setEarlyOpen] = useState(false);
  const [earlyReason, setEarlyReason] = useState("");
  const [emergencyMode, setEmergencyMode] = useState(false);
  const token = localStorage.getItem("bsb_token");
  const photoUrl = (pid) => `${process.env.REACT_APP_BACKEND_URL}/api/visits/${id}/photos/${pid}/download?auth=${token}`;
  const onPhoto = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setPhotoBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/visits/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo added"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setPhotoBusy(false); e.target.value = ""; }
  };
  const delPhoto = async (pid) => {
    try { await api.delete(`/visits/${id}/photos/${pid}`); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delVisit = async () => {
    if (!window.confirm(`Delete this visit for ${visit.client_name}? This cannot be undone.`)) return;
    try { await api.delete(`/visits/${id}`); toast.success("Visit deleted"); navigate("/roster"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancelVisit = async () => {
    const reason = window.prompt(`Cancel this visit for ${visit.client_name}? Add a short reason (optional) — the befriender will be notified.`);
    if (reason === null) return;
    try { await api.post(`/visits/${id}/cancel`, { reason }); toast.success("Visit cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = () =>
    api.get(`/visits/${id}`).then((r) => {
      setVisit(r.data);
      setTasks(r.data.tasks || []);
      setNotes(r.data.notes || "");
      setMileage(r.data.mileage || 0);
      api.get(`/clients/${r.data.client_id}`).then((c) => setClient(c.data)).catch(() => {});
    });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!visit)
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" /></div>;

  const unlocked = visit.status === "in_progress" || (visit.status === "completed" && !isCarer);
  const canCheckin = visit.status === "scheduled" || (isCarer && visit.status === "missed");
  const needsRead = isCarer && canCheckin && !visit.care_plan_read_at;
  const planShown = Boolean(client && (unlocked || client.pre_visit));
  const markRead = async () => {
    try { await api.post(`/visits/${id}/care-plan-read`); toast.success("Thank you — you can check in when you arrive"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const restricted = isCarer && visit.status === "completed";
  const toggle = (tid) => setTasks((t) => t.map((x) => (x.id === tid ? { ...x, done: !x.done } : x)));

  const saveLog = async () => {
    setSaving(true);
    try { await api.put(`/visits/${id}/log`, { tasks, notes, mileage: parseFloat(mileage) || 0 }); toast.success("Visit notes saved"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const postCheckin = (coords) =>
    api.post(`/visits/${id}/checkin`, coords)
      .then(() => { toast.success("You've arrived — care details unlocked"); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLocating(false));

  const doCheckin = () => {
    if (!navigator.geolocation) {
      toast.error("Location isn't available on this device — for data protection you can only check in at the client's home.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => postCheckin({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => { setLocating(false); toast.error("Location permission is needed — you can only check in when you're at the client's home."); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const doCheckout = async (earlyReasonText, emergency = false) => {
    await saveLog();
    try {
      await api.post(`/visits/${id}/checkout`, { early_reason: earlyReasonText || "", emergency });
      toast.success("Checked out — visit completed"); setEarlyOpen(false); setEmergencyMode(false); load();
    } catch (e) {
      if (e?.response?.status === 403) { setEmergencyMode(true); setEarlyOpen(true); toast.error(apiError(e)); }
      else { toast.error(apiError(e)); }
    }
  };
  const onCheckoutClick = () => {
    if (tasks.length === 0 || tasks.some((t) => !t.done)) { setEmergencyMode(false); setEarlyOpen(true); return; }
    doCheckout();
  };
  const genAi = async () => {
    setAiLoading(true);
    try { const { data } = await api.post(`/visits/${id}/ai-summary`); setVisit((v) => ({ ...v, ai_summary: data.ai_summary })); toast.success("AI summary generated"); }
    catch (err) { toast.error(apiError(err)); } finally { setAiLoading(false); }
  };

  const directions = client?.latitude != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${client.latitude},${client.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client?.address || visit.client_name)}`;
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  return (
    <div className="space-y-6" data-testid="visit-detail-page">
      <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-sm font-medium text-ink-soft hover:text-brand">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-ink">{visit.client_name}</h1>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone(visit.status)}`}>{visit.status.replace("_", " ")}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-ink-soft">
              <span className="flex items-center gap-1.5"><Clock size={15} />{visit.date} · {visit.start_time}–{visit.end_time}</span>
              {visit.service && <span data-testid="visit-service-badge" className="rounded-full bg-brand-light px-2 py-0.5 text-xs font-semibold text-brand">{visit.service}</span>}
              <span className="flex items-center gap-1.5"><User size={15} />{visit.carer_name}</span>
              {canManage && visit.status === "scheduled" && (
                <span data-testid="care-plan-read-status" className={`rounded-full px-2 py-0.5 text-xs font-semibold ${visit.care_plan_read_at ? "bg-sage-light text-sage-dark" : "bg-gold-light text-gold-dark"}`}>
                  {visit.care_plan_read_at ? "Care plan & risks read" : "Care plan not read yet"}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/incidents", { state: { reportForClient: visit.client_id, date: visit.date } })} data-testid="visit-report-incident-button"
              className="inline-flex items-center gap-2 rounded-lg border border-coral bg-coral-light px-4 py-2.5 font-semibold text-coral-dark transition-all hover:-translate-y-0.5 hover:bg-coral hover:text-white">
              <TriangleAlert size={18} /> Report incident
            </button>
            {canCheckin && (
              <button onClick={doCheckin} disabled={locating || needsRead} data-testid="checkin-button" title={needsRead ? "Read the care plan and risk assessment first" : ""}
                className="inline-flex items-center gap-2 rounded-lg bg-sage px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-sage-dark disabled:opacity-60">
                {locating ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />} {visit.status === "missed" ? "Check in (arrived late)" : "Check in on arrival"}
              </button>
            )}
            {visit.status === "in_progress" && (
              <button onClick={onCheckoutClick} data-testid="checkout-button"
                className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
                <LogOut size={18} /> Check out
              </button>
            )}
            {canManage && !["completed", "cancelled"].includes(visit.status) && (
              <button onClick={cancelVisit} data-testid="cancel-visit-button"
                className="inline-flex items-center gap-2 rounded-lg border border-gold-dark px-4 py-2.5 font-semibold text-gold-dark transition-all hover:-translate-y-0.5 hover:bg-gold-light">
                <X size={18} /> Cancel visit
              </button>
            )}
            {user?.role === "admin" && (
              <button onClick={delVisit} data-testid="delete-visit-button"
                className="inline-flex items-center gap-2 rounded-lg border border-coral px-4 py-2.5 font-semibold text-coral transition-all hover:-translate-y-0.5 hover:bg-coral hover:text-white">
                <Trash2 size={18} /> Delete visit
              </button>
            )}
          </div>
        </div>
      </div>

      {isCarer && canCheckin && visit.client_has_location === false && (
        <div data-testid="no-location-banner" className="flex items-start gap-3 rounded-xl border border-coral/40 bg-coral-light p-4">
          <TriangleAlert size={20} className="mt-0.5 shrink-0 text-coral-dark" />
          <p className="text-sm text-ink"><b>Check-in isn't possible yet:</b> {visit.client_name}'s home location hasn't been set. Please ask the office to add it (Clients → Edit → address and location).</p>
        </div>
      )}

      {needsRead && (
        <div data-testid="read-care-plan-banner" className="flex items-start gap-3 rounded-xl border border-gold/50 bg-gold-light p-4">
          <TriangleAlert size={20} className="mt-0.5 shrink-0 text-gold-dark" />
          <div className="flex-1">
            <p className="text-sm text-ink"><b>{visit.care_plan_changed_at ? "Updated — please re-read:" : "Before this visit:"}</b> {visit.care_plan_changed_at ? `the office has changed ${visit.client_name}'s care plan or risk assessment. Please read it again below` : `please read ${visit.client_name}'s care plan and risk assessment below`}, then tap <b>"I have read the care plan and risk assessment"</b>. You can't check in until you've done this.</p>
            {!planShown && <button onClick={markRead} data-testid="banner-confirm-care-plan-read" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"><CheckCircle2 size={16} /> I have read the care plan and risk assessment</button>}
          </div>
        </div>
      )}

      {restricted ? (
        <div data-testid="gdpr-restricted-notice" className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <Lock size={20} className="mt-0.5 shrink-0 text-brand" />
            <div>
              <h2 className="font-heading text-lg font-semibold text-ink">Visit completed — details closed for data protection</h2>
              <p className="mt-1 text-sm text-ink-soft">You've checked out of this visit, so the client's personal details are no longer shown. You can still see the visit date and who it was with above. If you need access again, please contact the office.</p>
            </div>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Location & entry details */}
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-ink">Location & entry</h2>
              <a href={directions} target="_blank" rel="noreferrer" data-testid="directions-link"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slateblue hover:underline">
                <Navigation size={15} /> Directions
              </a>
            </div>
            <p className="flex items-start gap-2 text-sm text-ink-soft"><MapPin size={16} className="mt-0.5 shrink-0 text-ink-muted" />{client?.address || "—"}</p>

            {!unlocked ? (
              <div data-testid="locked-care-info" className="mt-4 flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-ivory/60 p-6 text-center">
                <Lock size={22} className="text-ink-muted" />
                <p className="text-sm text-ink-soft">For data protection, the key-safe code and entry details unlock only once your phone confirms you're at the client's home. Please check in on arrival.</p>
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-lg bg-brand-light/70 p-4">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand"><KeyRound size={13} /> Key safe code</p>
                  <p data-testid="key-safe-code" className="mt-1 font-heading text-2xl font-bold tracking-widest text-ink">{client?.key_safe_code || "—"}</p>
                </div>
                <div className="rounded-lg bg-ivory p-4">
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><DoorOpen size={13} /> Access notes</p>
                  <p className="mt-1 text-sm text-ink">{client?.access_notes || "—"}</p>
                </div>
              </div>
            )}
          </div>

          {/* Care plan (unlocked) */}
          {(unlocked || client?.pre_visit) && client && (
            <div className="rounded-xl border border-border bg-white p-6 shadow-sm" data-testid="care-plan-section">
              <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Care plan{client.pre_visit && !unlocked ? " — read before your visit" : ""}</h2>
              <div className="mb-3 flex flex-wrap gap-2"><ClientFlags c={client} detailed /></div>
              <p className="whitespace-pre-wrap text-sm text-ink">{client.care_plan || "—"}</p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <p className="flex items-start gap-2 text-sm text-ink-soft"><HeartPulse size={15} className="mt-0.5 shrink-0 text-ink-muted" /><span><b>Needs:</b> {client.care_needs || "—"}</span></p>
                <p className="flex items-start gap-2 text-sm text-ink-soft"><AlertCircle size={15} className="mt-0.5 shrink-0 text-ink-muted" /><span><b>Conditions:</b> {client.medical_conditions || "—"}</span></p>
                <p className="flex items-start gap-2 text-sm text-ink-soft"><AlertCircle size={15} className="mt-0.5 shrink-0 text-coral" /><span><b>Allergies:</b> {client.allergies || "None recorded"}</span></p>
              </div>
              {client.risks?.length > 0 && (
                <div className="mt-4" data-testid="visit-risks">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-coral"><TriangleAlert size={13} /> Risk assessment</p>
                  <div className="space-y-2">
                    {client.risks.map((r) => (
                      <div key={r.id} className="rounded-lg border border-border bg-ivory/60 p-3">
                        <div className="flex items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${r.level === "high" ? "bg-coral text-white" : r.level === "medium" ? "bg-gold text-white" : "bg-sage text-white"}`}>{r.level}</span>
                          <span className="text-sm font-semibold text-ink">{r.category}</span>
                        </div>
                        {r.description && <p className="mt-1 text-sm text-ink-soft">{r.description}</p>}
                        {r.controls && <p className="mt-1 text-xs text-ink-muted"><b>Controls:</b> {r.controls}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {client.pre_visit && !client.risks?.length && <p className="mt-4 text-xs text-ink-muted" data-testid="no-risks-note">No risk assessment has been recorded yet — ask the office if you have any concerns.</p>}
              {isCarer && canCheckin && (visit.care_plan_read_at ? (
                <p className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-sage-dark" data-testid="care-plan-read-confirmed"><CheckCircle2 size={16} /> You confirmed you read this on {new Date(visit.care_plan_read_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
              ) : (
                <button onClick={markRead} data-testid="confirm-care-plan-read" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 font-semibold text-white hover:bg-brand-dark"><CheckCircle2 size={18} /> I have read the care plan and risk assessment</button>
              ))}
            </div>
          )}

          {/* Activities / tasks */}
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Activities & care tasks</h2>
            <div className="space-y-2">
              {tasks.map((t) => (
                <button key={t.id} onClick={() => toggle(t.id)} data-testid={`task-${t.id}`}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${t.done ? "border-sage/40 bg-sage-light" : "border-border bg-ivory/50 hover:bg-secondary"}`}>
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${t.done ? "border-sage bg-sage text-white" : "border-ink-muted"}`}>
                    {t.done && <CheckCircle2 size={14} />}
                  </span>
                  <span className={`text-sm font-medium ${t.done ? "text-sage-dark line-through" : "text-ink"}`}>{t.label}</span>
                </button>
              ))}
              {tasks.length === 0 && <p className="text-sm text-ink-muted">No tasks on this visit.</p>}
            </div>
          </div>

          {/* Notes + mileage */}
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-heading text-lg font-semibold text-ink">Visit notes</h2>
            <textarea data-testid="visit-notes-input" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="How was the visit? Note mood, wellbeing, anything to hand over…" className={input} />
            <div className="mt-4 flex flex-wrap items-end gap-4">
              {/phone/i.test(visit.service || "") ? (
                <div className="rounded-lg bg-ivory px-3 py-2 text-xs font-medium text-ink-muted" data-testid="phone-no-mileage-note">Telephone companion — no mileage</div>
              ) : visit.carer_is_driver === false ? (
                <div className="rounded-lg bg-ivory px-3 py-2 text-xs font-medium text-ink-muted" data-testid="non-driver-note">Non-driver — mileage not tracked</div>
              ) : (
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink">Miles from the previous client's house</label>
                  <p className="mb-1 max-w-xs text-xs text-ink-muted" data-testid="mileage-help">Paid at 45p a mile, separately from your hourly pay. Home to your first client and last client home are not paid.</p>
                  <input type="number" step="0.5" min="0" data-testid="mileage-input" value={mileage} onChange={(e) => setMileage(e.target.value)} className="w-32 rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
                </div>
              )}
              <button onClick={saveLog} disabled={saving} data-testid="save-log-button"
                className="inline-flex items-center gap-2 rounded-lg border border-brand px-4 py-2.5 font-semibold text-brand transition-colors hover:bg-brand-light disabled:opacity-60">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save notes
              </button>
            </div>
          </div>

          {/* Photos */}
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-ink">Visit photos</h2>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
                <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="visit-photo-input" onChange={onPhoto} />
                {photoBusy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Add photo
              </label>
            </div>
            {(visit.photos?.length || 0) === 0 ? (
              <p className="text-sm text-ink-muted">No photos yet. Snap a quick photo so the family can see how the visit went.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {visit.photos.map((p) => (
                  <div key={p.id} data-testid={`photo-${p.id}`} className="group relative overflow-hidden rounded-lg border border-border">
                    <img src={photoUrl(p.id)} alt={p.caption || "visit"} className="h-32 w-full object-cover" />
                    <button onClick={() => delPhoto(p.id)} data-testid={`delete-photo-${p.id}`}
                      className="absolute right-1 top-1 rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* AI summary */}
        <div className="h-fit rounded-xl border border-border bg-gradient-to-br from-brand to-brand-dark p-6 text-white shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={20} className="text-gold" />
            <h2 className="font-heading text-lg font-semibold">AI visit summary</h2>
          </div>
          <p className="mb-4 text-sm text-white/70">Turn the tasks and notes above into a professional handover note.</p>
          {visit.ai_summary ? (
            <div data-testid="ai-summary-text" className="whitespace-pre-wrap rounded-lg bg-white/10 p-4 text-sm leading-relaxed text-white/95">{visit.ai_summary}</div>
          ) : (
            <div className="rounded-lg border border-dashed border-white/30 p-6 text-center text-sm text-white/60">No summary yet.</div>
          )}
          <button onClick={genAi} disabled={aiLoading} data-testid="generate-ai-button"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 py-2.5 font-semibold text-brand-dark transition-all hover:-translate-y-0.5 hover:bg-gold-dark hover:text-white disabled:opacity-60">
            {aiLoading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            {visit.ai_summary ? "Regenerate summary" : "Generate summary"}
          </button>
        </div>
      </div>
      )}
      {earlyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEarlyOpen(false)} />
          <div data-testid="early-checkout-modal" className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-1 font-heading text-xl font-bold text-ink">{emergencyMode ? "Emergency check-out" : "End visit early?"}</h2>
            <p className="mb-4 text-sm text-ink-soft">{emergencyMode ? "This visit hasn't reached its end time. Only check out now if it's a genuine emergency — the office and admin will be alerted immediately. Please give the reason." : "Not all planned activities are ticked. Please give a short reason for ending this visit early — it will be saved on the visit and in the activity log."}</p>
            <textarea rows={3} data-testid="early-checkout-reason" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" placeholder="e.g. Client felt unwell and asked me to leave; office informed." value={earlyReason} onChange={(e) => setEarlyReason(e.target.value)} />
            <div className="mt-5 flex gap-3">
              <button onClick={() => { setEarlyOpen(false); setEmergencyMode(false); }} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button onClick={() => (earlyReason.trim() ? doCheckout(earlyReason.trim(), emergencyMode) : toast.error("Please add a brief reason"))} data-testid="confirm-early-checkout" className={`flex-1 rounded-lg py-2.5 font-semibold text-white ${emergencyMode ? "bg-coral hover:brightness-105" : "bg-brand hover:bg-brand-dark"}`}>{emergencyMode ? "Emergency check-out" : "End visit early"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
